import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiMessageSquare, FiSearch, FiFilter } from 'react-icons/fi';
import { getDemandes } from '../utils/api';
import './Demandes.css';

const Demandes = () => {
  const [demandes, setDemandes] = useState([]);
  const [filteredDemandes, setFilteredDemandes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    loadDemandes();
    const interval = setInterval(loadDemandes, 30000);
    return () => clearInterval(interval);
  }, []);

  // Si aucune demande après 2 secondes, proposer de générer les données
  useEffect(() => {
    const timer = setTimeout(() => {
      if (demandes.length === 0 && !loading) {
        // Essayer de générer les données
        const apiUrl = process.env.REACT_APP_API_URL || '/api';
        fetch(`${apiUrl}/generate-data`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' }
        })
        .then(res => res.json())
        .then(data => {
          if (data.success || data.message) {
            setTimeout(() => loadDemandes(), 1000);
          }
        })
        .catch(err => console.error('Erreur génération données:', err));
      }
    }, 2000);
    return () => clearTimeout(timer);
  }, [demandes.length, loading]);

  useEffect(() => {
    filterDemandes();
  }, [demandes, searchTerm, statusFilter]);

  const loadDemandes = async () => {
    try {
      const response = await getDemandes();
      setDemandes(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Erreur lors du chargement des demandes:', error);
      setLoading(false);
    }
  };

  const filterDemandes = () => {
    let filtered = demandes;

    if (statusFilter !== 'all') {
      filtered = filtered.filter(d => d.status === statusFilter);
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(d =>
        d.projet.toLowerCase().includes(term) ||
        d.nom.toLowerCase().includes(term) ||
        d.prenom.toLowerCase().includes(term) ||
        d.entreprise?.toLowerCase().includes(term) ||
        d.email.toLowerCase().includes(term)
      );
    }

    setFilteredDemandes(filtered);
  };

  const getStatusBadge = (status) => {
    const badges = {
      en_attente: { label: 'En Attente', class: 'badge-warning' },
      en_cours: { label: 'En Cours', class: 'badge-info' },
      termine: { label: 'Terminé', class: 'badge-success' },
    };
    return badges[status] || { label: status, class: 'badge-default' };
  };

  const getPrioriteBadge = (priorite) => {
    const badges = {
      basse: { label: 'Basse', class: 'priorite-basse' },
      normale: { label: 'Normale', class: 'priorite-normale' },
      haute: { label: 'Haute', class: 'priorite-haute' },
      urgente: { label: 'Urgente', class: 'priorite-urgente' },
    };
    return badges[priorite] || { label: priorite, class: 'priorite-normale' };
  };

  if (loading) {
    return <div className="loading">Chargement des demandes...</div>;
  }

  return (
    <div className="demandes-page">
      <div className="page-header">
        <div>
          <h2>Demandes Clients</h2>
          <p>{filteredDemandes.length} demande(s) trouvée(s)</p>
        </div>
      </div>

      <div className="filters-bar">
        <div className="search-box">
          <FiSearch />
          <input
            type="text"
            placeholder="Rechercher une demande..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <FiFilter />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">Tous les statuts</option>
            <option value="en_attente">En Attente</option>
            <option value="en_cours">En Cours</option>
            <option value="termine">Terminé</option>
          </select>
        </div>
      </div>

      <div className="demandes-grid">
        {filteredDemandes.length === 0 ? (
          <div className="empty-state">
            <p>Aucune demande trouvée</p>
          </div>
        ) : (
          filteredDemandes.map((demande) => {
            const statusBadge = getStatusBadge(demande.status);
            const prioriteBadge = getPrioriteBadge(demande.priorite);
            return (
              <Link
                key={demande.id}
                to={`/demandes/${demande.id}`}
                className="demande-card"
              >
                <div className="demande-card-header">
                  <h3>{demande.projet}</h3>
                  <span className={`badge ${statusBadge.class}`}>
                    {statusBadge.label}
                  </span>
                </div>
                <div className="demande-card-body">
                  <div className="client-info">
                    <div className="client-avatar">
                      {demande.prenom[0]}{demande.nom[0]}
                    </div>
                    <div>
                      <p className="client-name">
                        {demande.prenom} {demande.nom}
                      </p>
                      <p className="client-email">{demande.email}</p>
                    </div>
                  </div>
                  {demande.entreprise && (
                    <p className="entreprise">{demande.entreprise}</p>
                  )}
                  <p className="description">{demande.description}</p>
                </div>
                <div className="demande-card-footer">
                  <div className="demande-meta">
                    <span className="budget">💰 {demande.budget}</span>
                    <span className="delai">⏱️ {demande.delai}</span>
                    {demande.nb_messages > 0 && (
                      <span className="messages">
                        <FiMessageSquare /> {demande.nb_messages}
                      </span>
                    )}
                  </div>
                  <span className={`priorite ${prioriteBadge.class}`}>
                    {prioriteBadge.label}
                  </span>
                </div>
              </Link>
            );
          })
        )}
      </div>
    </div>
  );
};

export default Demandes;

